import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { getByPath } from './getByPath'

/**
 * オブジェクトの配列から指定されたパスの値が等しい最初の要素を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param items - オブジェクトの配列。
 * @param path - パス。
 * @param value - 値。
 * @returns 検索結果のオブジェクト。
 */
export function findByPath<T extends FieldValues, P extends FieldPath<T>>(
  items: T[],
  path: P,
  value: FieldPathValue<T, P> | null | undefined,
): T | undefined

/**
 * オブジェクトの配列から指定されたパスの値が等しい最初の要素を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param path - パス。
 * @param value - 値。
 * @returns オブジェクトの配列を引数に取り、検索結果のオブジェクトを返す関数。
 */
export function findByPath<T extends FieldValues, P extends FieldPath<T>>(
  path: P,
  value: FieldPathValue<T, P> | null | undefined,
): (items: T[]) => T | undefined

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function findByPath<T extends FieldValues, P extends FieldPath<T>>(
  ...args:
    | [P, FieldPathValue<T, P> | null | undefined]
    | [T[], P, FieldPathValue<T, P> | null | undefined]
): ((items: T[]) => T | undefined) | (T | undefined) {
  const findByPathInner = (
    items: T[],
    path: P,
    value: FieldPathValue<T, P> | null | undefined,
  ) => items.find((item) => getByPath(item, path) === value)

  if (args.length === 2) {
    return (items: T[]) => findByPathInner(items, ...args)
  }

  return findByPathInner(...args)
}
